import { ERROR_REQUIRED } from '../../../../../../shared/constants/validationErrors';

function slot(row) {
  return `${String(row?.sendCondition ?? '').trim()}|${String(row?.day ?? '').trim()}`;
}

// The backend flags one row of a duplicate (the later index). Re-target that slot
// message onto the row the user is editing when it's part of the same conflict, so
// the message follows the edit instead of the backend's index. Cross-template
// conflicts have no in-form twin, so they stay on their own row.
function pickSlotErrorRow(rows, flaggedIndex, lastEditedIndex) {
  const edited = rows[lastEditedIndex];
  const flagged = rows[flaggedIndex];
  if (!edited || !flagged || lastEditedIndex === flaggedIndex) return flaggedIndex;
  const sameSlot = slot(edited) === slot(flagged) && String(edited.sendCondition ?? '').trim();
  return sameSlot ? lastEditedIndex : flaggedIndex;
}

// Per-row / per-field errors for the Send Conditions rows, merging:
//  - `requiredByRow`: per-row, per-field client-side required check. Each entry is
//    `{ sendCondition, day }` of booleans, so only a field that is both required and
//    touched lights up (filling one field of a row won't flag the other, still-empty one);
//  - `backendRowErrors`: index-aligned errors from the live validation:
//      { sendCondition } — field error;
//      { nonField }      — row-level slot error (duplicate / cross-template) — the
//                          message goes under Send condition, both fields light up.
export function buildSendConditionRowErrors({
  sendConditions,
  requiredByRow = [],
  backendRowErrors = {},
  lastEditedIndex = -1,
}) {
  const rows = Array.isArray(sendConditions) ? sendConditions : [];

  // Resolve which row each slot-conflict message belongs to (the edited row when possible).
  const slotMessageByRow = {};
  Object.keys(backendRowErrors).forEach((key) => {
    const index = Number(key);
    const message = backendRowErrors[index]?.nonField;
    if (!message) return;
    const target = pickSlotErrorRow(rows, index, lastEditedIndex);
    slotMessageByRow[target] = slotMessageByRow[target] || message;
  });

  return rows.map((row, index) => {
    const backend = backendRowErrors[index] ?? {};
    const required = requiredByRow[index] ?? {};
    const sendConditionEmpty = String(row?.sendCondition ?? '').trim().length === 0;
    const dayEmpty = String(row?.day ?? '').trim().length === 0;
    const slotMessage = slotMessageByRow[index] || '';

    return {
      sendCondition:
        (required.sendCondition && sendConditionEmpty ? ERROR_REQUIRED : '') ||
        backend.sendCondition ||
        slotMessage ||
        '',
      day: required.day && dayEmpty ? ERROR_REQUIRED : '',
      // A slot conflict (send condition + day) lights up both fields.
      dayInvalid: Boolean(slotMessage),
    };
  });
}
